import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { ScrollArea } from '../../../components/ui/scroll-area';
import {
  FileText,
  Download,
  Loader2,
  Eye,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * DocumentPreviewModal - Enhanced document preview supporting multiple file types
 */
const DocumentPreviewModal = ({
  isOpen,
  onClose,
  previewDocument,
  previewContent,
  previewLoading,
  handleDownload,
  getPreviewUrl,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
            <Eye className="w-5 h-5 text-red-600" />
            {previewDocument?.name}
            {previewDocument?.file_type && (
              <Badge variant="outline" className="text-xs border-slate-400 dark:border-slate-500 text-slate-600 dark:text-slate-400 ml-2">
                {previewDocument.file_type.toUpperCase()}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {previewLoading ? (
            <div className="flex items-center justify-center h-96">
              <Loader2 className="w-8 h-8 animate-spin text-red-500" />
            </div>
          ) : previewContent ? (
            <div className="rounded-lg overflow-hidden border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800">
              {/* PDF Preview */}
              {(previewContent.type === 'pdf' || previewContent.file_type?.toLowerCase() === 'pdf') && (
                <iframe src={getPreviewUrl()} className="w-full h-[70vh]" title="PDF Preview" />
              )}
              
              {/* Image Preview */}
              {(previewContent.type === 'image' || ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(previewContent.file_type?.toLowerCase())) && (
                <div className="flex items-center justify-center p-4 bg-slate-100 dark:bg-slate-800">
                  <img 
                    src={previewContent.content ? `data:image/${previewContent.file_type};base64,${previewContent.content}` : getPreviewUrl()} 
                    alt={previewDocument?.name} 
                    className="max-w-full max-h-[70vh] object-contain" 
                  />
                </div>
              )}
              
              {/* Text Preview */}
              {previewContent.type === 'text' && (
                <ScrollArea className="h-[70vh] p-4">
                  <pre className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-mono">{previewContent.content}</pre>
                </ScrollArea>
              )}
              
              {/* Word Document Preview */}
              {previewContent.type === 'docx' && (
                <ScrollArea className="h-[70vh] p-4">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-red-600 text-sm mb-4">
                      <FileText className="w-4 h-4" />
                      <span>Word Document - {previewContent.paragraph_count} paragraphs, {previewContent.table_count} tables</span>
                    </div>
                    
                    {/* Paragraphs */}
                    {previewContent.paragraphs && previewContent.paragraphs.length > 0 && (
                      <div className="space-y-2">
                        {previewContent.paragraphs.map((para, idx) => (
                          <p key={idx} className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{para}</p>
                        ))}
                      </div>
                    )}
                    
                    {/* Tables */}
                    {previewContent.tables && previewContent.tables.length > 0 && (
                      <div className="space-y-4 mt-6">
                        <h4 className="text-sm font-medium text-red-600">Tables</h4>
                        {previewContent.tables.map((table, tableIdx) => (
                          <div key={tableIdx} className="overflow-x-auto border border-slate-300 dark:border-slate-600 rounded">
                            <table className="min-w-full text-sm">
                              <tbody>
                                {table.map((row, rowIdx) => (
                                  <tr key={rowIdx} className={rowIdx === 0 ? 'bg-slate-200' : 'bg-slate-100 dark:bg-slate-800/80'}>
                                    {row.map((cell, cellIdx) => (
                                      <td key={cellIdx} className="px-3 py-2 border-b border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {previewContent.paragraphs?.length === 0 && previewContent.tables?.length === 0 && (
                      <p className="text-slate-600 dark:text-slate-400 text-center py-8">No text content found in this document.</p>
                    )}
                  </div>
                </ScrollArea>
              )}
              
              {/* Unsupported or Error */}
              {(previewContent.type === 'unsupported' || previewContent.type === 'error') && (
                <div className="flex flex-col items-center justify-center h-96 text-slate-600 dark:text-slate-400">
                  <FileText className="w-16 h-16 mb-4 text-slate-400" />
                  <p className="text-lg font-medium">{previewContent.message}</p>
                  <p className="text-sm mt-2">Click Download to view the document.</p>
                </div>
              )}
              
              {/* Fallback for old format (file_content) */}
              {!previewContent.type && previewContent.file_content && (
                <>
                  {previewContent.file_type?.toLowerCase() === 'pdf' ? (
                    <iframe src={getPreviewUrl()} className="w-full h-[70vh]" title="PDF Preview" />
                  ) : (
                    <div className="flex items-center justify-center p-4 bg-slate-100 dark:bg-slate-800">
                      <img src={getPreviewUrl()} alt={previewDocument?.name} className="max-w-full max-h-[70vh] object-contain" />
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-96 text-slate-600 dark:text-slate-400">Unable to load preview</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} className="rounded-full bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-100">Close</Button>
          <Button onClick={() => previewDocument && handleDownload(previewDocument)} className="rounded-full bg-red-600 hover:bg-red-700 text-slate-900 dark:text-slate-100">
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentPreviewModal;
